import React, { Fragment, useMemo, useState } from 'react';
import StandardPage from '../../components/StandardPage';
import { Dialog, Transition } from '@headlessui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { useOrganization } from '../../context/organization';
import { handleApiError } from '../../api-client/error_handler';
import { useQueryMetadataContext } from '../../context/query-metadata';
import axios from 'axios';

type Plan = {
  title: string;
  price: string;
  yearly: number;
  description: string;
  countriesAllowed: number | 'all';
  slug: string;
  featured?: boolean;
};

const PLANS: Plan[] = [
  { title: '1 Country Access', price: '€6,000/year', yearly: 6000, description: 'Access to 1 fixed country.', countriesAllowed: 1, slug: 'acled-1' },
  { title: '2 Countries Access', price: '€11,500/year', yearly: 11500, description: 'Access to 2 fixed countries.', countriesAllowed: 2, slug: 'acled-2' },
  { title: '3 Countries Access', price: '€16,500/year', yearly: 16500, description: 'Access to 3 fixed countries.', countriesAllowed: 3, slug: 'acled-3', featured: true },
  { title: 'All Countries Access', price: '€21,000/year', yearly: 21000, description: 'Access to the full ACLED catalog.', countriesAllowed: 'all', slug: 'acled-all' }
];

const AcledLicensePage: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [activePlan, setActivePlan] = useState<Plan | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<Array<{ value: string, label: string }>>([]);
  const [search, setSearch] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { organization, setOrganization } = useOrganization();
  const { data: queryMetadata } = useQueryMetadataContext();
  const localityOptions = queryMetadata.localities;

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return localityOptions;
    return localityOptions.filter(c => c.label.toLowerCase().includes(q));
  }, [search, localityOptions]);

  const onChoosePlan = (plan: Plan) => {
    setActivePlan(plan);
    setSelectedCountries([]);
    setOpen(true);
  };

  const canConfirm = useMemo(() => {
    if (!activePlan) return false;
    if (activePlan.countriesAllowed === 'all') return localityOptions.length > 0;
    return selectedCountries.length === activePlan.countriesAllowed;
  }, [activePlan, selectedCountries, localityOptions]);

  const confirm = async () => {
    if (!activePlan) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const countries = activePlan.countriesAllowed === 'all'
        ? localityOptions.slice(0, 4).map(c => c.value) // Pass any 4 or the first 4 countries
        : selectedCountries.map(c => c.value);
  
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/acled-license`,
        { countries },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (data.invoice_url != null) {
        window.location.assign(data.invoice_url);
      } else {
        toast.success("Invoice created! Please check your email for payment instructions.");
      }
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const handleCancel = async () => {
    if (!activePlan) return;

    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!activePlan) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/acled-license`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("License cancelled successfully.");
      // Reset organization state to remove the current license
      setOrganization((prevOrg) => prevOrg ? ({
        ...prevOrg,
        has_all_countries: false,
        acled_countries: []
      }) : null);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
      setCancelDialogOpen(false);
    }
  };

  return (
    <StandardPage title="ACLED Data Access">
      <div className="w-full mx-auto bg-gradient-to-br from-white to-gray-50 shadow-2xl rounded-3xl px-6 sm:px-10 lg:px-12 py-10 sm:py-14">
        <div className="flex flex-col items-center text-center">
          <p className="max-w-3xl mb-6 sm:mb-8 text-lg sm:text-xl text-gray-700">
            Choose the package that fits your coverage needs. Once you select your country set, it remains fixed for the duration of the subscription.
          </p>

          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm text-blue-800 ring-1 ring-blue-100">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6z"/><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/></svg>
            <span>Licenses are annual and billed upfront.</span>
          </div>
        </div>

        <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <div
              key={plan.slug}
              className={[
                'relative flex flex-col justify-between rounded-2xl border bg-white p-7 shadow-sm transition hover:shadow-lg',
                plan.featured ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-200'
              ].join(' ')}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white shadow">
                  Most popular
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{plan.title}</h2>
                <p className="mt-3 text-3xl font-extrabold text-gray-900">{plan.price}</p>
                <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                <ul className="mt-5 space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <svg className="h-5 w-5 text-emerald-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    {plan.countriesAllowed === 'all'
                      ? <span>Includes all ACLED countries</span>
                      : <span>Select exactly {plan.countriesAllowed} fixed {plan.countriesAllowed === 1 ? 'country' : 'countries'}</span>}
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="h-5 w-5 text-emerald-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    <span>Full year of access · SLA-backed</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={() => {
                  if (organization?.has_all_countries && plan.countriesAllowed === 'all' || organization?.acled_countries?.length === plan.countriesAllowed) {
                    handleCancel();
                  } else {
                    onChoosePlan(plan);
                  }
                }}
                className={[
                  'mt-6 w-full rounded-xl px-5 py-3 font-semibold transition',
                  organization?.has_all_countries && plan.countriesAllowed === 'all'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : organization?.acled_countries?.length === plan.countriesAllowed
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : plan.featured
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-900 text-white hover:bg-black'
                ].join(' ')}
              >
                {organization?.has_all_countries && plan.countriesAllowed === 'all'
                  ? 'Cancel License'
                  : organization?.acled_countries?.length === plan.countriesAllowed
                    ? 'Cancel License'
                    : (plan.countriesAllowed === 'all' ? 'Request Purchase' : 'Select & Purchase')}
              </button>
            </div>
          ))}
        </div>

        {/* Fine print */}
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">License Terms</h3>
            <p className="mt-2 text-sm text-gray-600">
              Countries are fixed for the term, or if you change the license. You can renew with a different selection at the next cycle.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Delivery</h3>
            <p className="mt-2 text-sm text-gray-600">
              Access is provisioned to your CivAPI organization. We support programmatic and dashboard usage.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Need Assistance?</h3>
            <p className="mt-2 text-sm text-gray-600">If you're unsure about which license to choose or have any questions, we're here to help.</p>
            <a
              href="mailto:sales@civapi.com?subject=ACLED%20Plan%20Assistance"
              className="mt-3 inline-flex rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </div>

      {/* Modal for selecting countries */}
      <Transition show={open} as={Fragment}>
        <Dialog onClose={setOpen} className="relative z-[9999]">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-gray-100">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {activePlan?.title}
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    {activePlan?.countriesAllowed === 'all'
                      ? 'Confirm your request and we’ll reach out to finalize your all-countries license.'
                      : `Select exactly ${activePlan?.countriesAllowed} ${activePlan?.countriesAllowed === 1 ? 'country' : 'countries'}. Your selection will be fixed for the term.`}
                  </p>

                  {activePlan?.countriesAllowed !== 'all' && (
                    <div className="mt-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <input
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search countries…"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                        />
                        <div className="text-xs text-gray-500 whitespace-nowrap px-2">
                          {selectedCountries.length}/{activePlan?.countriesAllowed}
                        </div>
                      </div>

                      {/* Multi-select as tags + list */}
                      <div className="mb-3 flex flex-wrap gap-2">
                        {selectedCountries.map((c) => (
                          <span key={c.value} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800 ring-1 ring-blue-100">
                            {c.label}
                            <button
                              type="button"
                              onClick={() => setSelectedCountries((s) => s.filter((x) => x !== c))}
                              className="ml-1 text-blue-700 hover:text-blue-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {selectedCountries.length === 0 && (
                          <span className="text-xs text-gray-500">No countries selected yet</span>
                        )}
                      </div>

                      <div className="max-h-64 overflow-auto rounded-lg border border-gray-200">
                        <ul className="divide-y divide-gray-100">
                          {filteredCountries.map((c) => {
                            const picked = selectedCountries.includes(c);
                            const limitReached =
                              activePlan &&
                              activePlan.countriesAllowed !== 'all' &&
                              selectedCountries.length >= activePlan.countriesAllowed &&
                              !picked;
                            return (
                              <li key={c.value}>
                                <button
                                  type="button"
                                  disabled={!!limitReached}
                                  onClick={() =>
                                    setSelectedCountries((s) =>
                                      picked ? s.filter((x) => x !== c) : [...s, c]
                                    )
                                  }
                                  className={[
                                    'flex w-full items-center justify-between px-3 py-2 text-sm',
                                    picked ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50',
                                    limitReached ? 'cursor-not-allowed opacity-40' : ''
                                  ].join(' ')}
                                >
                                  <span>{c.label}</span>
                                  {picked && (
                                    <svg className="h-4 w-4 text-blue-700" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                    </svg>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      onClick={() => setOpen(false)}
                      className="rounded-full bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirm}
                      disabled={!canConfirm || loading}
                      className={[
                        'rounded-full px-5 py-2 text-sm font-semibold text-white',
                        canConfirm ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed',
                        loading ? 'cursor-not-allowed opacity-50' : ''
                      ].join(' ')}
                    >
                      {loading ? 'Processing...' : (activePlan?.countriesAllowed === 'all' ? 'Continue' : 'Confirm Selection')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal for cancel confirmation */}
      <Transition show={cancelDialogOpen} as={Fragment}>
        <Dialog onClose={setCancelDialogOpen} className="relative z-[9999]">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-gray-100">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Confirm Cancellation
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Are you sure you want to cancel the license?
                  </p>
                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      onClick={() => setCancelDialogOpen(false)}
                      className="rounded-full bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-300"
                    >
                      No
                    </button>
                    <button
                      onClick={confirmCancel}
                      disabled={loading}
                      className={[
                        'rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700',
                        loading ? 'cursor-not-allowed opacity-50' : ''
                      ].join(' ')}
                    >
                      {loading ? 'Processing...' : 'Yes, Cancel'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </StandardPage>
  );
};

export default AcledLicensePage;
